import {
  FormattedMessage,
  FormattedDate,
  defineMessages,
  useIntl
} from 'react-intl';
import { Box, Text, Anchor, List, Li, H1 } from '../../components';
import { useSettingsQuery } from '../../lib/core/SettingsQuery';
import orderTypes from './orderTypes';
import {
  orderQuery_order_paymentTransactions,
  OrderType,
  ReceiptOrder,
  PaymentTransactionStatus,
  orderQuery_order_walletTransactions
} from '../../core-types';
import { EmailHeader } from '../EmailRender/EmailRenderLayout';
import Head from 'next/head';
import { BlockContent } from '../../components/BlockContent';
import { useStaticDataSystem } from '../../lib/core/staticDataQuery';

function getNotFoundError(msg) {
  const err = new Error(msg);
  err.code = 'ENOENT';
  return err;
}

const messages = defineMessages({
  header: {
    id: 'receipt',
    defaultMessage: 'Receipt'
  }
});

// Get transactionNumber by finding the first paymentTransaction with a transactionNumber
function getTransactionNumber(
  paymentTransactions:
    | orderQuery_order_paymentTransactions[]
    | orderQuery_order_walletTransactions[]
): string | undefined {
  if (paymentTransactions) {
    for (const pt of paymentTransactions) {
      if (
        pt.__typename === 'PaymentTransaction' &&
        pt.transactionNumber &&
        pt.status === PaymentTransactionStatus.completed
      ) {
        return pt.transactionNumber;
      }
      if (pt.__typename === 'WalletTransaction' && pt.id) {
        return pt.id;
      }
    }
  }
  return undefined;
}

function Receipt({
  order,
  showHeader = true
}: {
  order: ReceiptOrder | null;
  showHeader?: boolean;
}) {
  const intl = useIntl();

  const settingsQueryProps = useSettingsQuery();
  const staticDataSystem = useStaticDataSystem();
  const loading = settingsQueryProps.loading;

  if (loading) {
    return null;
  }

  if (!order) {
    throw getNotFoundError('Order not found');
  }

  if (!settingsQueryProps.data || !settingsQueryProps.data.settings) {
    throw getNotFoundError('Settings not found');
  }

  const { settings } = settingsQueryProps.data;

  const footer = settings && settings.footer;

  const transactionNumber = getTransactionNumber(
    order.paymentTransactions.length > 0
      ? order.paymentTransactions
      : order.walletTransactions
  );

  const OrderTable = orderTypes[order.type];

  if (!OrderTable) {
    throw new Error(`Renderer for ${order.type} not found`);
  }

  const isVerification =
    order.type === OrderType.verification ||
    (order.type === OrderType.business_registration_fee && order.amount === 0);

  return (
    <>
      <Head>
        <title>{intl.formatMessage(messages.header)}</title>
      </Head>
      {showHeader && (
        <EmailHeader
          sanitySettings={settings}
          companyName={order && order.company && order.company.name}
        />
      )}
      {!isVerification && (
        <>
          <H1 fontSize={2} mb={2}>
            <FormattedMessage
              {...messages.header}
              values={{ siteName: settings.title }}
            />
          </H1>
          <Text as="p" mute>
            <FormattedMessage
              id="modules.Receipt.orderNumber"
              defaultMessage="Order #{id}"
              values={{ id: order.id }}
            />
          </Text>
        </>
      )}

      <Box mb={2}>
        <OrderTable order={order} />
      </Box>
      {!isVerification && (
        <Box mb={4}>
          <List>
            {/* {order.user && order.user.name && (
                  <Li>
                    <FormattedMessage
                      id="modules.Receipt.payedBy"
                      defaultMessage="Payed by: {name}"
                      values={{
                        name: order.user.name
                      }}
                    />
                  </Li>
                )} */}
            <Li key="transactionNumber">
              {/* It was decided not to show this line because it doesn't make sense. This is usually considered a bug by users */}
              {/* {!transactionNumber && (
                <FormattedMessage
                  id="could_not_load_payment_transaction"
                  defaultMessage="Could not load payment transaction"
                />
              )} */}
              {transactionNumber && (
                <FormattedMessage
                  id="transaction_number_str"
                  defaultMessage="Transaction number: {transactionNumber}"
                  values={{
                    transactionNumber
                  }}
                />
              )}
            </Li>
            {order.completedAt && (
              <Li key={order.completedAt}>
                <FormattedMessage
                  id="date_completed_at"
                  defaultMessage="Date: {completedAt}"
                  values={{
                    completedAt: (
                      <FormattedDate
                        value={order.completedAt}
                        year="numeric"
                        month="long"
                        day="2-digit"
                        hour="numeric"
                        minute="numeric"
                      />
                    )
                  }}
                />
              </Li>
            )}
            {order.creditCardMasked && (
              <Li
                key={order.creditCardMasked}
                data-testid="Receipt__creditCardMasked"
              >
                <FormattedMessage
                  id="card_creditCardMasked"
                  defaultMessage="Card: {creditCardMasked}"
                  values={{
                    creditCardMasked: order.creditCardMasked
                  }}
                />
              </Li>
            )}
          </List>
        </Box>
      )}
      {staticDataSystem.contactEmail && (
        <Text textAlign="center" as="p" mb={4}>
          <FormattedMessage
            id="modules.Receipt.contact"
            defaultMessage="For customer service or other inquiries, please send us an email at {contactEmail}"
            values={{
              contactEmail: (
                <Anchor
                  color="text"
                  href={`mailto:${staticDataSystem.contactEmail}`}
                >
                  {staticDataSystem.contactEmail}
                </Anchor>
              )
            }}
          />
        </Text>
      )}
      {footer && footer.aboutOperator && (
        <Text textAlign="center" small mute>
          <BlockContent blocks={footer.aboutOperator} />
        </Text>
      )}
    </>
  );
}

export default Receipt;
